import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import FlexContainer from '../../atoms/FlexContainer';
import InputField_T from '../../atoms/InputField';
import ButtonT from '../../atoms/Button';
import { ExclamationTriangle, Info } from '../../../images/FaIcons';
import CrossSvg from '../../../images/CrossSvg';

const InputField = styled(InputField_T)`
  margin-bottom: 5px;
`;

const ModalContainer = styled.div`
  position: absolute;
  width: 300px;
  padding: 1em;
  box-shadow: 0 0 16px 0 rgba(0,0,0,0.12);
  background-color: white;
  border-radius: 4px;
  top: -5px;
  left: -20px;
  z-index: 3;
  &:after {
    right: 100%;
    top: 18%;
    border: solid transparent;
    content: " ";
    height: 0;
    width: 0;
    position: absolute;
    pointer-events: none;
    border-color: rgba(136, 183, 213, 0);
    border-right-color: #fff;
    border-width: 10px;
    margin-top: -10px;
  }
`;

const ModeToggle = styled.div`
  display: flex;
  gap: 8px;
  margin: 10px 0;
`;

const ModeButton = styled.button`
  appearance: none;
  border: 1px solid #D7D9D3;
  background: ${props => (props.active ? '#F3F4F2' : '#FFFFFF')};
  color: ${props => (props.active ? '#3C3D3A' : '#666')};
  border-radius: 4px;
  padding: 6px 8px;
  font-size: 12px;
  font-family: 'Petasense Open Sans';
  font-weight: 600;
  cursor: pointer;
  flex: 1;
  &:hover {
    background: #F3F4F2;
  }
`;

const InfoRow = styled(FlexContainer)`
  padding: 0.5em 0;
`;

const SvgIcon = styled.span`
  width: max-content;
  &:hover {
    cursor: ${props => (props.nohover ? 'default' : 'pointer')};
  }
  ${props => props.marginright && `margin-right: ${props.marginright};`}
`;

const Label = styled.label`
  font-size: 12px;
  font-family: 'Petasense Open Sans';
  color: #999B95;
  font-weight: 600;
  margin-bottom: 0;
  ${props => props.header && `
    font-size: 18px;
    font-weight: 400;
  `}
  ${props => props.color && `color: ${props.color};`}
`;

const SmallValue = styled.span`
  font-size: 12px;
  color: #666;
  font-weight: 600;
`;

/**
 * Modal for setting the Y-axis upper limit in either absolute value or % of peak.
 * Used by LineChart HOC.
 */
const YAxisLimitModal = ({
  mode,
  yAxisUpperLimit,
  yAxisPercent,
  visiblePeakY,
  computedYMaxFromPercent,
  maxYdomain,
  minYdomain,
  error,
  formatAxisLimitNumber,
  onClose,
  onModeChange,
  onAbsoluteChange,
  onPercentChange,
  onSave,
  onKeyPress,
  isSaveDisabled,
}) => {
  const { t } = useTranslation();

  return (
    <ModalContainer>
      <FlexContainer justifyContent="space-between">
        <Label header>{t('charts.yAxisLimitModal.title')}</Label>
        <SvgIcon onClick={onClose}>
          <CrossSvg width={20} height={20} />
        </SvgIcon>
      </FlexContainer>
      <ModeToggle>
        <ModeButton
          type="button"
          active={mode === 'absolute'}
          onClick={() => onModeChange('absolute')}
          title={t('charts.yAxisLimitModal.modes.absoluteTooltip')}
        >
          {t('charts.yAxisLimitModal.modes.absolute')}
        </ModeButton>
        <ModeButton
          type="button"
          active={mode === 'percent'}
          onClick={() => onModeChange('percent')}
          title={t('charts.yAxisLimitModal.modes.percentTooltip')}
        >
          {t('charts.yAxisLimitModal.modes.percentOfPeak')}
        </ModeButton>
      </ModeToggle>

      {mode === 'percent' ? (
        <>
          <InputField
            type="text"
            value={yAxisPercent}
            onChange={onPercentChange}
            onKeyPress={onKeyPress}
            placeholder={t('charts.yAxisLimitModal.percentPlaceholder')}
          />
          <InfoRow>
            <SvgIcon nohover marginright="5px"><Info color="#999B95" /></SvgIcon>
            <FlexContainer direction="column">
              <Label>
                {t('charts.yAxisLimitModal.peakVisible')}{' '}
                <SmallValue>
                  {Number.isFinite(visiblePeakY) ? formatAxisLimitNumber(visiblePeakY) : t('common.na')}
                </SmallValue>
              </Label>
              <Label>
                {t('charts.yAxisLimitModal.computedYMax')}{' '}
                <SmallValue>
                  {Number.isFinite(computedYMaxFromPercent) ? formatAxisLimitNumber(computedYMaxFromPercent) : t('common.na')}
                </SmallValue>
              </Label>
            </FlexContainer>
          </InfoRow>
        </>
      ) : (
        <InputField
          type="text"
          value={yAxisUpperLimit}
          onChange={onAbsoluteChange}
          onKeyPress={onKeyPress}
        />
      )}

      <InfoRow>
        <SvgIcon nohover marginright="5px"><Info color="#999B95" /></SvgIcon>
        <FlexContainer direction="column">
          <Label>{t('charts.yAxisLimitModal.maxAllowed', { value: formatAxisLimitNumber(maxYdomain) })}</Label>
          <Label>{t('charts.yAxisLimitModal.minAllowed', { value: formatAxisLimitNumber(minYdomain) })}</Label>
        </FlexContainer>
      </InfoRow>

      {error && (
        <FlexContainer>
          <SvgIcon nohover marginright="5px">
            <ExclamationTriangle color="red" />
          </SvgIcon>
          <Label color="red">{error}</Label>
        </FlexContainer>
      )}

      <FlexContainer justifyContent="flex-start" margintop="5px">
        <Fragment>
          <ButtonT disabled={isSaveDisabled} onClick={onSave}>
            {t('common.save')}
          </ButtonT>
        </Fragment>
      </FlexContainer>
    </ModalContainer>
  );
};

YAxisLimitModal.propTypes = {
  mode: PropTypes.oneOf(['absolute', 'percent']).isRequired,
  yAxisUpperLimit: PropTypes.string.isRequired,
  yAxisPercent: PropTypes.string.isRequired,
  visiblePeakY: PropTypes.number,
  computedYMaxFromPercent: PropTypes.number,
  maxYdomain: PropTypes.number,
  minYdomain: PropTypes.number,
  error: PropTypes.string,
  formatAxisLimitNumber: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onModeChange: PropTypes.func.isRequired,
  onAbsoluteChange: PropTypes.func.isRequired,
  onPercentChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onKeyPress: PropTypes.func.isRequired,
  isSaveDisabled: PropTypes.bool.isRequired,
};

YAxisLimitModal.defaultProps = {
  visiblePeakY: null,
  computedYMaxFromPercent: null,
  maxYdomain: undefined,
  minYdomain: undefined,
  error: '',
};

export default YAxisLimitModal;
